import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Modal, Row, Col } from 'react-bootstrap'
import { Button } from 'src/components'
import { faPlus, faWindowClose } from '@fortawesome/free-solid-svg-icons'

import { CardProviderName } from 'src/enums/CardProviderEnum'

import {
  actions,
  createCardPaymentDevice,
  deleteCardPaymentDevice,
  getCardPaymentDevices,
  updateCardPaymentDevice,
} from 'src/actions/cardProvider.actions'

import { loadingSelector } from 'src/selectors/loading.selector'
import { haveAnyValue } from 'src/utils/utilitiesV2'
import {
  handlerError,
  handlerInfoWithButtons,
  handlerSuccess,
  hasErrors,
} from 'src/selectors/error.selector'
import { showAlert } from 'src/actions/alert.actions'

import {
  CardProviderDeviceDetail,
  ICardProviderDeviceDetailProps,
} from 'src/content/CardProviders/CardProvider/devices/CardProviderDeviceDetail'
import { CardProviderDeviceList } from 'src/content/CardProviders/CardProvider/devices/CardProviderDeviceList'
import { isAllowed } from 'src/selectors/modules.selector'
import { cardProviderPermissions } from 'src/enums/permissions'

interface IFlags {
  createAndUpdate: boolean
}

interface ICardProviderDevicesProps {
  providerId: number
  onClose: () => void
}

export const CardProviderDevices = ({
  providerId,
  onClose,
}: ICardProviderDevicesProps) => {
  const dispatch = useDispatch()

  const loadingCreateOrUpdate = useSelector(state =>
    loadingSelector([actions.CRUD_CARD_PAYMENT_DEVICE])(state),
  )
  const errorCreateOrUpdate = useSelector(state =>
    hasErrors([actions.CRUD_CARD_PAYMENT_DEVICE])(state),
  )

  const canCreate = useSelector(state =>
    isAllowed(state, [cardProviderPermissions.createDevices]),
  )

  const show = haveAnyValue(providerId)
  const providerName = show ? CardProviderName[providerId] : ''

  const [flags, setFlags] = useState<IFlags>({
    createAndUpdate: false,
  })

  const [deviceModal, setDeviceModal] = useState<ICardProviderDeviceDetailProps>({
    show: false,
  })

  useEffect(() => {
    if (!show) return
    setUp()
  }, [show])

  useEffect(() => {
    if (loadingCreateOrUpdate) setFlags({ ...flags, createAndUpdate: true })
    else if (flags.createAndUpdate) {
      setFlags({ ...flags, createAndUpdate: false })

      const alert = errorCreateOrUpdate
        ? handlerError(errorCreateOrUpdate.message)
        : {
            ...handlerSuccess(),
            onConfirm: () => setUp(),
          }

      dispatch(showAlert(alert))
    }
  }, [loadingCreateOrUpdate])

  const setUp = () => {
    dispatch(getCardPaymentDevices(providerId))
  }

  const onSaveDevice = (device: ICardProviderDeviceRequest) => {
    setDeviceModal({ show: false })
    if (!device.id) dispatch(createCardPaymentDevice(device))
    else dispatch(updateCardPaymentDevice(device))
  }

  const onUpdate = (item: ICardProviderDeviceDetail) => {
    setDeviceModal({
      show: true,
      data: item,
    })
  }

  const onDelete = (item: ICardProviderDeviceDetail) => {
    dispatch(
      showAlert({
        ...handlerInfoWithButtons(
          'Eliminar dispositivo',
          `¿Está seguro de eliminar el dispositivo: ${item.device.name} con código: ${item.device.code}?`,
        ),
        onConfirm: () => {
          dispatch(deleteCardPaymentDevice(item.device.id))
        },
      }),
    )
  }

  return (
    <div>
      <Modal show={show && !deviceModal.show} centered onHide={onClose}>
        <Modal.Header closeButton={!loadingCreateOrUpdate}>
          <Modal.Title>Dispositivos: {providerName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            {canCreate && (
              <Col xl={12}>
                <Row className={'container-buttons'}>
                  <Button
                    icon={faPlus}
                    color={'accent'}
                    onClick={() => setDeviceModal({ show: true })}
                    disabled={loadingCreateOrUpdate}>
                    Crear
                  </Button>
                </Row>
              </Col>
            )}
            <Col xl={12}>
              <CardProviderDeviceList onUpdate={onUpdate} onDelete={onDelete} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Row className={'container-buttons'}>
            <Button
              icon={faWindowClose}
              color={'secondary'}
              onClick={onClose}
              disabled={loadingCreateOrUpdate}>
              Cerrar
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>

      <CardProviderDeviceDetail
        {...deviceModal}
        onClose={() => setDeviceModal({ show: false })}
        onSave={onSaveDevice}
      />
    </div>
  )
}
